import React, { useState } from "react";
import {
  ActionIcon,
  Paper,
  rem,
  Text,
  Title,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { LikeResponse } from "../../../entities/components/LikeResponse";
import "../Report.scss";
import useReportStore from "../../../store/useReportStore";
import { useMediaQuery } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { JobPositionData } from "../../../types/api";
import { getJobDetailsByTitle } from "../../../api/reportApi";
import { JobDetailsModal } from "../../../utils/components/JobDetailsModal";
import { InfoTooltipIcon } from "../../../shared/svg/InfoTooltopIcon";
import useProfileStore from "../../../store/useProfileStore";

export const HigherPositions: React.FC = () => {
  const { report, reportId } = useReportStore();
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);
  const [currentJob, setCurrentJob] = useState<JobPositionData | null>(null);
  const matches = useMediaQuery("(max-width: 1100px)");
  const theme = useMantineTheme();

  const commonStyles: React.CSSProperties = {
    backgroundColor: theme.colors.gray[1],
    color: theme.colors.dark[7],
    fontSize: rem(14),
    padding: `${rem(4)} ${rem(8)}`,
    borderRadius: rem(4),
    boxShadow: theme.shadows.sm,
    maxWidth: rem(500),
    wordWrap: "break-word" as "break-word",
    whiteSpace: "normal",
    border: "1px solid gray",
  };

  const { profileLanguage } = useProfileStore();

  if (!report?.higher_positions?.data?.length) return null;

  const handleOpen = async (title: string) => {
    try {
      const res = await getJobDetailsByTitle(
        title,
        Object.keys(profileLanguage!)[0]
      );
      if (res) {
        setCurrentJob(res.data);
        setOpened(true);
      }
    } catch (err) {
      console.log(err);
    }
  };
  return (
    <Paper radius="24px" p="32px" mt={32} withBorder>
      <Title size={matches ? 24 : 36} mb={8}>
        {report.higher_positions.mark_down
          ? report.higher_positions.mark_down.replace("##", "")
          : ""}
      </Title>
      <Text style={{ fontSize: matches ? 16 : 18 }} mb={24} c="dimmed">
        {report?.higher_positions?.explanation
          ? report?.higher_positions?.explanation
          : ""}
      </Text>
      <div className="piramid">
        {report?.higher_positions?.data.map((position, index) => (
          <div className={`triangle triangle-${index + 1}`} key={index}>
            <Text>{position.title}</Text>
          </div>
        ))}
      </div>
      {report?.higher_positions?.data?.map((position, index) => (
        <Text mt={24} key={index}>
          <b>{position.title} </b>
          <Tooltip
            label={t("learn_more")}
            withArrow
            transitionProps={{ duration: 100, transition: "fade" }}
            arrowSize={8}
            arrowOffset={8}
            position="top"
            offset={6}
            styles={{
              tooltip: commonStyles,
              arrow: {
                backgroundColor: theme.colors.gray[1],
                border: "1px solid gray",
              },
            }}
          >
            <ActionIcon
              variant="subtle"
              size="sm"
              c="gray"
              style={{ verticalAlign: "middle" }}
              aria-label="More actions"
              onClick={() => handleOpen(position.title)}
            >
              <InfoTooltipIcon size={20} />
            </ActionIcon>
          </Tooltip>
          : {position.description}
        </Text>
      ))}
      <JobDetailsModal
        opened={opened}
        job={currentJob!}
        onClose={() => {
          setOpened(false);
          setCurrentJob(null);
        }}
        setCurrentJob={setCurrentJob}
      />
      <LikeResponse report_id={reportId} section="higher_positions" />
    </Paper>
  );
};
